// Hall of Fame lists — pure functions, no Firebase.
// The summary document holds three lists of at most CAP entries, one entry per player:
//   all   = the best scores ever;
//   month = the players of the last 30 days;
//   week  = the players of the last 7 days.
// A score is inserted when it is sent (one read and one write of the summary), never by recounting every player. A score
// pushed out of a list never comes back: the lists are approximate by design (30 slots to show 5 or 10).
// entry = { id, emoji, nickname, city?, countryCode?, score, date } (date: ISO 8601, when the score was achieved)

export const CAP = 30;
const DAY_MS = 24 * 3600 * 1000;
export const WINDOWS = Object.freeze({ all: Infinity, month: 30 * DAY_MS, week: 7 * DAY_MS });

/**
 * Inserts one score into one list.
 * - A player is listed once: their entry is replaced if it has expired, or if the new score is higher.
 * - Not full: the score enters.
 * - Full: it takes the place of the oldest expired entry, whatever its score; if none has expired, it must beat the lowest
 *   score, and replaces it.
 * @param {object[]} list
 * @param {object} entry
 * @param {number} windowMs Infinity for `all`
 * @param {number} now ms
 * @returns {object[]} a new list, or `list` itself when nothing changes
 */
export function insert(list, entry, windowMs, now) {
  const age = (e) => now - new Date(e.date).getTime();
  const next = list.slice();
  const own = next.findIndex((e) => e.id === entry.id);
  if (own >= 0) {
    if (age(next[own]) <= windowMs && entry.score <= next[own].score) return list;
    next[own] = entry;
    return next;
  }
  if (next.length < CAP) { next.push(entry); return next; }
  let oldest = -1;
  next.forEach((e, i) => { if (age(e) > windowMs && (oldest < 0 || new Date(e.date) < new Date(next[oldest].date))) oldest = i; });
  if (oldest >= 0) { next[oldest] = entry; return next; }
  let lowest = 0;
  next.forEach((e, i) => { if (e.score < next[lowest].score) lowest = i; });
  if (entry.score > next[lowest].score) { next[lowest] = entry; return next; }
  return list;
}

/** An empty summary. */
export const emptySummary = () => ({ updated: null, all: [], month: [], week: [] });

/**
 * Inserts one score into the three lists of a summary.
 * @param {{all?: object[], month?: object[], week?: object[]}|null|undefined} summary
 * @returns {{updated: string, all: object[], month: object[], week: object[]}}
 */
export function insertScore(summary, entry, now) {
  const base = { ...emptySummary(), ...(summary ?? {}) };
  const lists = Object.fromEntries(Object.entries(WINDOWS)
    .map(([key, windowMs]) => [key, insert(Array.isArray(base[key]) ? base[key] : [], entry, windowMs, now)]));
  return { updated: new Date(now).toISOString(), ...lists };
}
